import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loginInput } from '@gql/shared-types';
import { api, ApiError } from '../api/client.js';
import { TurnstileWidget } from '../components/TurnstileWidget.js';

type FormInput = { email: string };

export function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = params.get('token');

  const [phase, setPhase] = useState<'request' | 'sent' | 'consuming' | 'error'>(
    token ? 'consuming' : 'request',
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<FormInput>({ mode: 'onBlur' });

  const consumedRef = useRef(false);

  useEffect(() => {
    if (!token || consumedRef.current) return;
    consumedRef.current = true;
    (async () => {
      try {
        await api.loginConsume(token);
        navigate('/dashboard', { replace: true });
      } catch (err) {
        setErrorMsg(err instanceof ApiError && err.code === 'invalid_or_expired_token'
          ? t('login.errInvalid') : t('login.errGeneric'));
        setPhase('error');
      }
    })();
  }, [token, navigate, t]);

  const onSubmit = handleSubmit(async (data) => {
    setErrorMsg(null);
    if (!turnstileToken) { setErrorMsg(t('login.captchaPlease')); return; }
    setSubmitting(true);
    try {
      const payload = loginInput.parse({ email: data.email, turnstileToken });
      await api.loginRequest(payload);
      setPhase('sent');
    } catch (err) {
      setErrorMsg(err instanceof ApiError && err.code === 'captcha_failed'
        ? t('login.errCaptcha') : t('login.errRequest'));
    } finally {
      setSubmitting(false);
    }
  });

  if (phase === 'consuming') {
    return (
      <div className="card">
        <h2 className="card-title">{t('login.consumingTitle')}</h2>
        <p className="card-subtitle">{t('login.wait')}</p>
      </div>
    );
  }

  if (phase === 'sent') {
    return (
      <div className="card">
        <h2 className="card-title">{t('login.sentTitle')}</h2>
        <div className="alert alert-info">{t('login.sentBody')}</div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="card">
        <h2 className="card-title">{t('login.errorTitle')}</h2>
        <div className="alert alert-error">{errorMsg}</div>
        <p className="muted-block">
          <Link to="/login" className="text-link">{t('login.newLink')}</Link>
          {' · '}
          <Link to="/register" className="text-link">{t('login.register')}</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="card-title">{t('login.title')}</h2>
      <p className="card-subtitle">{t('login.subtitle')}</p>

      {errorMsg && <div className="alert alert-error">{errorMsg}</div>}

      <form onSubmit={onSubmit} noValidate>
        <div className="field">
          <label className="field-label" htmlFor="email">{t('login.email')}</label>
          <input id="email" type="email" className="field-input" autoComplete="email"
            {...register('email', { required: t('login.required') })} />
          {errors.email && <p className="field-error">{errors.email.message}</p>}
        </div>

        <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken('')} />

        <button className="button" type="submit" disabled={submitting || !turnstileToken}>
          {submitting ? t('login.submitting') : t('login.submit')}
        </button>
      </form>

      <p className="muted-block">
        {t('login.noAccount')} <Link to="/register" className="text-link">{t('login.registerNow')}</Link>
      </p>
    </div>
  );
}
